export default () => {
  const SelectActions = {
    Close: 0,
    CloseSelect: 1,
    First: 2,
    Last: 3,
    Next: 4,
    Open: 5,
    PageDown: 6,
    PageUp: 7,
    Previous: 8,
    Select: 9,
    Type: 10,
  };

  function filterOptions(options = [], filter, exclude = []) {
    return options.filter(option => {
      const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
      return matches && exclude.indexOf(option) < 0;
    });
  }

  function getActionFromKey(event, menuOpen) {
    const { key, altKey, ctrlKey, metaKey } = event;
    const openKeys = ["ArrowDown", "ArrowUp", "Enter", " "];
    if (!menuOpen && openKeys.includes(key)) return SelectActions.Open;
    if (key === "Home") return SelectActions.First;
    if (key === "End") return SelectActions.Last;
    if (
      key === "Backspace" ||
      key === "Clear" ||
      (key.length === 1 && key !== " " && !altKey && !ctrlKey && !metaKey)
    ) {
      return SelectActions.Type;
    }
    if (menuOpen) {
      if (key === "ArrowUp" && altKey) return SelectActions.CloseSelect;
      if (key === "ArrowDown" && !altKey) return SelectActions.Next;
      if (key === "ArrowUp") return SelectActions.Previous;
      if (key === "PageUp") return SelectActions.PageUp;
      if (key === "PageDown") return SelectActions.PageDown;
      if (key === "Escape") return SelectActions.Close;
      if (key === "Enter" || key === " ") return SelectActions.CloseSelect;
    }
  }

  function getIndexByLetter(options, filter, startIndex = 0) {
    const orderedOptions = [
      ...options.slice(startIndex),
      ...options.slice(0, startIndex),
    ];
    const firstMatch = filterOptions(orderedOptions, filter)[0];
    const allSameLetter = array => array.every(letter => letter === array[0]);

    if (firstMatch) return options.indexOf(firstMatch);
    else if (allSameLetter(filter.split(""))) {
      const matches = filterOptions(orderedOptions, filter[0]);
      return options.indexOf(matches[0]);
    } else {
      return -1;
    }
  }

  function getUpdatedIndex(currentIndex, maxIndex, action) {
    const pageSize = 10;
    switch (action) {
      case SelectActions.First:
        return 0;
      case SelectActions.Last:
        return maxIndex;
      case SelectActions.Previous:
        return Math.max(0, currentIndex - 1);
      case SelectActions.Next:
        return Math.min(maxIndex, currentIndex + 1);
      case SelectActions.PageUp:
        return Math.max(0, currentIndex - pageSize);
      case SelectActions.PageDown:
        return Math.min(maxIndex, currentIndex + pageSize);
      default:
        return currentIndex;
    }
  }

  function isElementInView(element) {
    const bounding = element.getBoundingClientRect();
    return (
      bounding.top >= 0 &&
      bounding.left >= 0 &&
      bounding.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      bounding.right <=
        (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  function isScrollable(element) {
    return element && element.clientHeight < element.scrollHeight;
  }

  function maintainScrollVisibility(activeElement, scrollParent) {
    const { offsetHeight, offsetTop } = activeElement;
    const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;
    const isAbove = offsetTop < scrollTop;
    const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

    if (isAbove) {
      scrollParent.scrollTo(0, offsetTop);
    } else if (isBelow) {
      scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
    }
  }

  const Select = function (el, options = [], values = []) {
    this.el = el;
    this.labelEl = el.querySelector(".custom-dropdown__label");
    this.comboEl = el.querySelector("[role=combobox]");
    this.listboxEl = el.querySelector("[role=listbox]");
    this.quickSearchEl = el.closest(".quick-search");

    this.idBase = this.comboEl.id || "combo";
    this.options = options;
    this.values = values;
    this.activeIndex = undefined;
    this.open = false;
    this.searchString = "";
    this.searchTimeout = null;

    if (el && this.comboEl && this.listboxEl) this.init();
  };

  Select.prototype.init = function () {
    this.comboEl.innerHTML = this.options[0];
    const placeholder = this.comboEl.dataset.placeholder || "Select";
    this.comboEl.textContent = placeholder;

    this.comboEl.addEventListener("blur", this.onComboBlur.bind(this));
    this.listboxEl.addEventListener("focusout", this.onComboBlur.bind(this));
    this.comboEl.addEventListener("click", this.onComboClick.bind(this));
    this.comboEl.addEventListener("keydown", this.onComboKeyDown.bind(this));

    // Add listeners to pre-rendered option elements
    const optionEls = this.el.querySelectorAll("[role=option]");
    optionEls.forEach((optionEl, index) => {
      if (optionEl.dataset.isDefault) {
        this.activeIndex = index;
        this.comboEl.setAttribute("aria-activedescendant", index);
        this.comboEl.textContent = optionEl.textContent;

        if (this.quickSearchEl) {
          const hiddenInput = this.quickSearchEl.querySelector(
            "input.js-search-type-hidden",
          );
          if (hiddenInput) {
            hiddenInput.value = this.values[index];
          }
        }
      }
      optionEl.addEventListener("click", event => {
        event.stopPropagation();
        this.onOptionClick(index);
      });
      optionEl.addEventListener("mousedown", this.onOptionMouseDown.bind(this));
    });
  };

  Select.prototype.getSearchString = function (char) {
    if (typeof this.searchTimeout === "number")
      window.clearTimeout(this.searchTimeout);

    this.searchTimeout = window.setTimeout(() => {
      this.searchString = "";
    }, 500);

    this.searchString += char;
    return this.searchString;
  };

  Select.prototype.onComboBlur = function (event) {
    if (this.listboxEl.contains(event.relatedTarget)) return;

    if (this.open) {
      // Don't select the activeIndex is there is no activeIndex set by user
      // This prevents "defaulting" to index 0 if no option was selected
      // Required for nav-on-change to work right
      if (this.activeIndex !== undefined) {
        this.selectOption(this.activeIndex);
      }
      this.updateMenuState(false, false);
    }
  };

  Select.prototype.onComboClick = function () {
    this.updateMenuState(!this.open, false);
  };

  Select.prototype.onComboKeyDown = function (event) {
    const max = this.options.length - 1;
    const action = getActionFromKey(event, this.open);

    switch (action) {
      case SelectActions.First:
      case SelectActions.Last:
        this.updateMenuState(true);
      // fallthrough
      case SelectActions.Next:
      case SelectActions.Previous:
      case SelectActions.PageUp:
      case SelectActions.PageDown:
        event.preventDefault();

        const isForward =
          action === SelectActions.Next ||
          action === SelectActions.PageDown ||
          action === SelectActions.First;

        const currentIndex =
          typeof this.activeIndex === "number"
            ? this.activeIndex
            : isForward
              ? -1
              : max + 1;

        return this.onOptionChange(getUpdatedIndex(currentIndex, max, action));

      case SelectActions.CloseSelect:
        event.preventDefault();
        this.selectOption(this.activeIndex);
      // fallthrough
      case SelectActions.Close:
        event.preventDefault();
        return this.updateMenuState(false);

      case SelectActions.Type:
        return this.onComboType(event.key);

      case SelectActions.Open:
        event.preventDefault();
        return this.updateMenuState(true);
    }
  };

  Select.prototype.onComboType = function (letter) {
    this.updateMenuState(true);

    const searchString = this.getSearchString(letter);
    const searchIndex = getIndexByLetter(
      this.options,
      searchString,
      this.activeIndex + 1,
    );

    if (searchIndex >= 0) {
      this.onOptionChange(searchIndex);
    } else {
      window.clearTimeout(this.searchTimeout);
      this.searchString = "";
    }
  };

  Select.prototype.onOptionChange = function (index) {
    this.activeIndex = index;
    this.comboEl.setAttribute(
      "aria-activedescendant",
      `${this.idBase}-${index}`,
    );

    const options = this.el.querySelectorAll("[role=option]");
    [...options].forEach(optionEl =>
      optionEl.classList.remove("option-current"),
    );
    options[index].classList.add("option-current");

    if (isScrollable(this.listboxEl)) {
      maintainScrollVisibility(options[index], this.listboxEl);
    }

    if (!isElementInView(options[index])) {
      options[index].scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  Select.prototype.onOptionClick = function (index) {
    this.onOptionChange(index);
    this.selectOption(index);
    this.updateMenuState(false);
  };

  Select.prototype.onOptionMouseDown = function () {
    this.ignoreBlur = true;
  };

  Select.prototype.selectOption = function (index) {
    this.activeIndex = index;

    // update displayed value
    const selected = this.options[index];
    this.comboEl.textContent = selected;

    // update aria-selected
    const options = this.el.querySelectorAll("[role=option]");
    [...options].forEach(optionEl => {
      optionEl.setAttribute("aria-selected", "false");
    });
    options[index].setAttribute("aria-selected", "true");

    // Only scroll if the root element has .js-select-scrollTo
    if (this.el.classList.contains("js-select-scrollTo")) {
      const target = document.getElementById(selected);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    // Only navigate if the root element has .js-select-navOnChange
    if (this.el.classList.contains("js-select-navOnChange")) {
      const url = this.values[index];
      if (/\/api\/download\/pdf/i.test(url)) {
        var newTab = window.open('about:blank', '_blank');
        var loaderHtml = '<!DOCTYPE html><html><head><title>Loading PDF</title><style>' +
          '*{margin:0;box-sizing:border-box}' +
          'body{display:flex;flex-direction:column;align-items:center;justify-content:center;' +
          'min-height:100vh;background:#fff;font-family:Arial,Helvetica,sans-serif;color:#262626}' +
          '.ring{width:48px;height:48px;border:4px solid #e0e0e0;border-top-color:#007cbd;' +
          'border-radius:50%;animation:r .8s linear infinite}' +
          '@keyframes r{to{transform:rotate(360deg)}}' +
          'p{margin-top:16px;font-size:14px;color:#757575}' +
          '</style></head><body><div class="ring"></div><p>Preparing your document</p></body></html>';
        var doc = newTab.document;
        doc.open();
        doc.writeln(loaderHtml);
        doc.close();

        // Navigate directly to the PDF endpoint.
        // The API's Content-Disposition will provide the filename.
        newTab.location.href = url;
      } else if (/\/api\/download\//i.test(url)) {
        window.location.href = url;
      } else {
        window.location.href = url;
      }
    }

    // Updates the hidden input with the dropdown value
    if (this.quickSearchEl) {
      const hiddenInput = this.quickSearchEl.querySelector(
        "input.js-search-type-hidden",
      );
      if (hiddenInput) {
        hiddenInput.value = this.values[index];
      }
    }
  };

  Select.prototype.updateMenuState = function (open, callFocus = true) {
    if (this.open === open) return;

    this.open = open;
    this.comboEl.setAttribute("aria-expanded", `${open}`);
    open ? this.el.classList.add("open") : this.el.classList.remove("open");

    const activeID = open ? `${this.idBase}-${this.activeIndex}` : "";
    this.comboEl.setAttribute("aria-activedescendant", activeID);

    if (activeID === "" && !isElementInView(this.comboEl)) {
      this.comboEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    if (callFocus) this.comboEl.focus();
  };

  const selectEls = document.querySelectorAll(".js-select");

  selectEls.forEach(el => {
    const optionEls = el.querySelectorAll('[role="option"]');
    const options = [];
    const values = [];
    Array.from(optionEls).map((el, ind) => {
      options[ind] = el.textContent.trim();
      values[ind] = el.dataset.value.trim();
    });
    new Select(el, options, values);
  });
};
